import type { IconName } from '@travelata-libs/ui-kit';
import { HotelFeature, type HotelProduct } from '@/entities/hotel';
import {
  SERP_CARD_BEACH_LINE_ICON,
  SERP_CARD_BEACH_TYPE_ICON,
  SERP_CARD_DISTANCE_ICONS,
  SERP_CARD_FEATURE_ICONS,
} from '../config/featureIcons';
import {
  SERP_CARD_BEACH_TYPE_LABELS,
  SERP_CARD_DISTANCE_LABELS,
  SERP_CARD_FEATURE_LABELS,
  SERP_CARD_TEXTS,
} from '../config/texts';
import type { SerpCardDistance, SerpProduct } from '../model/types';

export interface SerpCardFeature {
  id: string;
  label: string;
  /** Иконку показывает только шторка: в карточке теги особенностей идут без иконок. */
  icon: `${IconName}`;
}

const BEACH_LINE_FEATURE_ID = 'beach-line';
const BEACH_TYPE_FEATURE_ID = 'beach-type';
const DISTANCE_FEATURE_ID_SUFFIX = '-distance';

const METERS_IN_KILOMETER = 1000;

const DISTANCE_FORMAT = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });

const SERP_CARD_DISTANCES: readonly SerpCardDistance[] = ['airport', 'beach', 'center'];

const SERP_CARD_FEATURE_ORDER: readonly string[] = [
  getDistanceFeatureId('beach'),
  getDistanceFeatureId('center'),
  BEACH_LINE_FEATURE_ID,
  BEACH_TYPE_FEATURE_ID,
  HotelFeature.AirConditioner,
  HotelFeature.Wifi,
  getDistanceFeatureId('airport'),
  HotelFeature.NearTheLift,
  HotelFeature.WithKids,
  HotelFeature.ForCouples,
  HotelFeature.AdultsOnly,
  HotelFeature.Wellness,
];

/**
 * Все особенности отеля в порядке приоритета: расстояния до пляжа и центра, линия пляжа, тип пляжа,
 * кондиционер, Wi-Fi, расстояние до аэропорта, затем остальные признаки отеля.
 * Список не обрезается — сколько тегов поместить в карточку, решает сама карточка.
 */
export function getSerpCardFeatures(product: SerpProduct): SerpCardFeature[] {
  const featuresById = new Map(
    [...getDistanceFeatures(product.hotel), ...getBeachFeatures(product.hotel), ...getHotelFeatures(product.hotel)].map(
      (feature) => [feature.id, feature],
    ),
  );

  return SERP_CARD_FEATURE_ORDER.flatMap((id) => featuresById.get(id) ?? []);
}

function getBeachFeatures({ beachLine, beachType }: HotelProduct): SerpCardFeature[] {
  const result: SerpCardFeature[] = [];

  if (isBeachLine(beachLine)) {
    result.push({
      id: BEACH_LINE_FEATURE_ID,
      label: `${beachLine}${SERP_CARD_TEXTS.beachLineSuffix}`,
      icon: SERP_CARD_BEACH_LINE_ICON,
    });
  }

  if (beachType !== null) {
    result.push({
      id: BEACH_TYPE_FEATURE_ID,
      label: SERP_CARD_BEACH_TYPE_LABELS[beachType],
      icon: SERP_CARD_BEACH_TYPE_ICON,
    });
  }

  return result;
}

function getHotelFeatures({ features }: HotelProduct): SerpCardFeature[] {
  return features.map((feature) => ({
    id: feature,
    label: SERP_CARD_FEATURE_LABELS[feature],
    icon: SERP_CARD_FEATURE_ICONS[feature],
  }));
}

function getDistanceFeatureId(distance: SerpCardDistance): string {
  return `${distance}${DISTANCE_FEATURE_ID_SUFFIX}`;
}

function getDistanceFeatures({ distances }: HotelProduct): SerpCardFeature[] {
  const {
    airport = null,
    beach = null,
    beachMax = null,
    center = null,
  }: Partial<HotelProduct['distances']> = distances ?? {};
  const formattedDistances: Record<SerpCardDistance, string | null> = {
    airport: isPositiveDistance(airport) ? formatKilometers(airport) : null,
    beach: formatMeters(isPositiveDistance(beach) ? beach : beachMax),
    center: formatMeters(center),
  };

  return SERP_CARD_DISTANCES.flatMap((distance) => {
    const formattedDistance = formattedDistances[distance];

    if (formattedDistance === null) return [];

    return [
      {
        id: getDistanceFeatureId(distance),
        label: `${formattedDistance} ${SERP_CARD_DISTANCE_LABELS[distance]}`,
        icon: SERP_CARD_DISTANCE_ICONS[distance],
      },
    ];
  });
}

function formatMeters(meters: number | null): string | null {
  if (!isPositiveDistance(meters)) return null;
  if (meters >= METERS_IN_KILOMETER) return formatKilometers(meters / METERS_IN_KILOMETER);

  return `${Math.round(meters)} ${SERP_CARD_TEXTS.meters}`;
}

function formatKilometers(kilometers: number): string {
  return `${DISTANCE_FORMAT.format(kilometers)} ${SERP_CARD_TEXTS.kilometers}`;
}

function isPositiveDistance(value: number | null): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

// Линия пляжа приходит нулём или отсутствует, когда она у отеля неизвестна.
function isBeachLine(value: number | null): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
